import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/helpers";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { WhatsappLogo, Copy, CheckCircle, PaperPlaneRight } from "@phosphor-icons/react";

const waUrl = (phone, text) => `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;

function BusinessNumber() {
  const [cfg, setCfg] = useState(null);
  const [num, setNum] = useState("");
  const [key, setKey] = useState("");
  useEffect(() => { api.get("/whatsapp/business").then(({ data }) => { setCfg(data); setNum(data.business_number); }).catch(() => {}); }, []);
  const save = async () => {
    try { const { data } = await api.post("/whatsapp/business", { business_number: num, pass_key: key || null }); setCfg(data); setKey(""); toast.success("WhatsApp number saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!cfg) return null;
  return (
    <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid="wa-business-card">
      <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Sending WhatsApp number</div>
      <div className="space-y-2">
        <div><Label className="text-xs text-[#6B6B6B]">Business WhatsApp Number</Label><Input value={num} onChange={(e) => setNum(e.target.value)} className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-business-number" /></div>
        <div><Label className="text-xs text-[#6B6B6B]">Pass Key {cfg.has_pass_key && <span className="text-[#2D6A4F]">(saved {cfg.pass_key_masked})</span>}</Label><Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Leave blank to keep existing" className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-business-passkey" /></div>
        <Button onClick={save} variant="outline" className="border-[#E6E2D6]" data-testid="wa-business-save">Save</Button>
      </div>
      <p className="text-[11px] text-[#8A8A8A] mt-3">Messages open in WhatsApp on this device with the text ready. Make sure this number is logged in to WhatsApp / WhatsApp Business here, then tap Send.</p>
    </Card>
  );
}

function AutoReminders({ status }) {
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/whatsapp/reminders").then(({ data }) => setLog(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/reminders/run");
      if (data.status === "skipped") toast.info("Automatic WhatsApp is not set up yet");
      else toast.success(`Reminders: ${data.sent} sent, ${data.failed} failed, ${data.already_sent} already sent`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const on = status?.auto_reminders;
  return (
    <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid="wa-auto-reminders">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-[#1A1A1A]">Automatic installment reminders</div>
        <span className={`text-[10px] px-2 py-0.5 rounded ${on ? "bg-[#E8F5E9] text-[#2D6A4F]" : "bg-[#FFF8E1] text-[#B8860B]"}`} data-testid="wa-auto-status">{on ? "ON" : "WAITING FOR TOKEN"}</span>
      </div>
      <p className="text-[11px] text-[#8A8A8A]">Every day at 10 AM: customers whose installment is due in 3 days or today get a WhatsApp reminder (once each).</p>
      <Button size="sm" variant="outline" onClick={run} disabled={busy || !on} className="mt-3 border-[#E6E2D6]" data-testid="wa-run-reminders">{busy ? "Sending…" : "Send today's reminders now"}</Button>
      <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
        {log.slice(0, 20).map((r) => (
          <div key={r.id} className="text-[11px] flex justify-between"><span>{r.customer_name} · {r.account_no} · {r.kind === "due_today" ? "due today" : "due in 3 days"}</span><span className={r.ok ? "text-[#2D6A4F]" : "text-[#9D2A2A]"}>{r.ok ? "sent" : "failed"}</span></div>
        ))}
      </div>
    </Card>
  );
}

export default function WhatsAppOffers() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/settings/whatsapp").then(({ data }) => setStatus(data)).catch(() => {}); }, []);
  const [audience, setAudience] = useState("all");
  const [list, setList] = useState([]);
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState({});
  const [history, setHistory] = useState([]);
  const [logged, setLogged] = useState(false);

  useEffect(() => { api.get(`/whatsapp/recipients?audience=${audience}`).then(({ data }) => setList(data)).catch(() => setList([])); setSent({}); setLogged(false); }, [audience]);
  const loadHistory = () => api.get("/whatsapp/broadcasts").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const personal = (c) => msg.replace(/\{name\}/gi, c.name);
  const send = async (c) => {
    if (!msg.trim()) return toast.error("Type the offer message first");
    window.open(waUrl(c.wa_phone, personal(c)), "_blank");
    setSent((s) => ({ ...s, [c.id]: true }));
    if (!logged) { setLogged(true); await api.post("/whatsapp/broadcasts", { message: msg, audience, recipient_count: list.length }).catch(() => {}); loadHistory(); }
  };
  const next = list.find((c) => !sent[c.id]);
  const copyNumbers = async () => {
    const text = list.map((c) => "+" + c.wa_phone).join(", ");
    try { await navigator.clipboard.writeText(text); toast.success(`${list.length} numbers copied`); }
    catch { window.prompt("Copy these numbers:", text); }
  };
  const sentCount = Object.keys(sent).length;
  const sendAll = async () => {
    if (!msg.trim()) return toast.error("Type the offer message first");
    if (!window.confirm(`Send this offer automatically to ${list.length} customers?`)) return;
    try { const { data } = await api.post("/whatsapp/broadcasts/send-all", { message: msg, audience }); toast.success(`Sending to ${data.recipient_count} customers…`); setTimeout(loadHistory, 1500); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="WhatsApp Offers" subtitle="Send offer messages to all customers from OM GOLD's WhatsApp." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs text-[#6B6B6B]">Send to</Label>
                <Select value={audience} onValueChange={setAudience}>
                  <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-audience"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="all">All customers</SelectItem><SelectItem value="scheme">Active scheme customers</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="text-sm text-[#4A4A4A]" data-testid="wa-recipient-count">{list.length} customers · {sentCount} opened</div>
              <Button variant="outline" onClick={copyNumbers} className="border-[#E6E2D6]" data-testid="wa-copy-numbers"><Copy size={15} className="mr-1" /> Copy all numbers</Button>
            </div>
            <Label className="text-xs text-[#6B6B6B] mt-4 block">Offer message (use {"{name}"} for the customer's name)</Label>
            <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} placeholder="Dear {name}, this Dhanteras get 10% off on making charges at OM GOLD!" className="mt-1 bg-white border-[#E6E2D6]" data-testid="wa-offer-message" />
            <div className="flex flex-wrap gap-2 mt-3">
              {status?.auto_offers && <Button onClick={sendAll} className="bg-[#1A1A1A] hover:bg-black text-white" data-testid="wa-send-all"><WhatsappLogo size={16} className="mr-1" /> Send to everyone automatically</Button>}
              <Button onClick={() => next && send(next)} disabled={!next} variant={status?.auto_offers ? "outline" : "default"} className={status?.auto_offers ? "border-[#E6E2D6]" : "bg-[#25D366] hover:bg-[#1EB257] text-white"} data-testid="wa-send-next"><PaperPlaneRight size={16} className="mr-1" /> {next ? `Send to next: ${next.name}` : "All messages opened"}</Button>
            </div>
            {!status?.auto_offers && <p className="text-[11px] text-[#8A8A8A] mt-2" data-testid="wa-auto-off-note">Automatic sending turns on once the WhatsApp Business token and offer template are saved in Settings.</p>}
          </Card>
          <Card className="bg-white border-[#E6E2D6] shadow-sm max-h-[480px] overflow-y-auto">
            {list.length === 0 ? <EmptyState label="No customers with phone numbers" /> : list.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-4 py-2.5 border-b border-[#F2EBE1]" data-testid={`wa-recipient-${c.id}`}>
                <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-[#8A8A8A]">+{c.wa_phone}</div></div>
                {sent[c.id] ? <span className="text-xs text-[#2D6A4F] flex items-center gap-1"><CheckCircle size={14} /> Opened</span>
                  : <Button size="sm" variant="ghost" onClick={() => send(c)} className="text-[#25D366]" data-testid={`wa-send-${c.id}`}><WhatsappLogo size={16} className="mr-1" /> Send</Button>}
              </div>
            ))}
          </Card>
        </div>
        <div className="space-y-4">
          <BusinessNumber />
          <AutoReminders status={status} />
          <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm" data-testid="wa-history">
            <div className="text-sm font-semibold text-[#1A1A1A] mb-3">Past offers</div>
            {history.length === 0 ? <div className="text-xs text-[#8A8A8A]">No offers sent yet.</div> : history.map((h) => (
              <div key={h.id} className="border-b border-[#F2EBE1] pb-2 mb-2"><div className="text-xs text-[#8A8A8A]">{fmtDate(h.created_at)} · {h.recipient_count} customers · {h.created_by}{h.mode === "auto" && ` · auto: ${h.sent || 0} sent, ${h.failed || 0} failed`}</div><div className="text-sm line-clamp-2">{h.message}</div></div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
